import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";

import prevButton from "../../img/prevb.svg";
import nextButton from "../../img/nextb.svg";
import whyImg from "../../img/Markets/why-trade.webp";
import Fade from 'react-reveal/Fade';

const items = [
  { titleKey: "markets.why.1.title", textKey: "markets.why.1.text" },
  { titleKey: "markets.why.2.title", textKey: "markets.why.2.text" },
  { titleKey: "markets.why.3.title", textKey: "markets.why.3.text" },
  { titleKey: "markets.why.4.title", textKey: "markets.why.4.text" },
];

const DIVIDER = "bg-[rgba(19,50,97,0.3)]";

function WhyTrade() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);

  return (
    <section className="mt-[80px] xl:mt-[160px]">
      {/* Desktop */}
      <div className="hidden xl:block">
        <div className="relative mx-auto h-[400px] max-w-[1220px] overflow-hidden bg-[#daebfc]">

          {/* Title block — top-left */}
          <Fade bottom>
          <div className="absolute left-[20px] top-[55px] flex w-[366px] flex-col gap-[20px]">
            <h2 className="font-['Nunito_Sans'] text-[32px] font-black leading-[1.5] text-[#3273b4]">
              {t("markets.why.title1")}
              <br />
              {t("markets.why.title2")}
            </h2>
            <span className="block h-[4px] w-[104px] bg-[#05193a]" />
          </div>

          </Fade>
          {/* Top-middle: item 1 */}
          <Fade bottom delay={100}>
          <div className="absolute left-[435px] top-[70px] w-[340px]">
            <p className="font-['Nunito_Sans'] text-[16px] font-black leading-[1.4] text-[#05193a]">
              {t(items[0].titleKey)}
            </p>
            <p className="mt-[10px] font-['Nunito_Sans'] text-[16px] font-light leading-[1.4] text-[#05193a]">
              {t(items[0].textKey)}
            </p>
          </div>

          </Fade>
          {/* Top-right: item 2 */}
          <Fade bottom delay={200}>
          <div className="absolute left-[842px] top-[70px] w-[340px]">
            <p className="font-['Nunito_Sans'] text-[16px] font-black leading-[1.4] text-[#05193a]">
              {t(items[1].titleKey)}
            </p>
            <p className="mt-[10px] font-['Nunito_Sans'] text-[16px] font-light leading-[1.4] text-[#05193a]">
              {t(items[1].textKey)}
            </p>
          </div>

          </Fade>
          {/* Bottom-left: item 3 */}
          <Fade bottom delay={150}>
          <div className="absolute left-[20px] top-[230px] w-[366px]">
            <p className="font-['Nunito_Sans'] text-[16px] font-black leading-[1.4] text-[#05193a]">
              {t(items[2].titleKey)}
            </p>
            <p className="mt-[10px] font-['Nunito_Sans'] text-[16px] font-light leading-[1.4] text-[#05193a]">
              {t(items[2].textKey)}
            </p>
          </div>

          </Fade>
          {/* Bottom-middle: item 4 */}
          <Fade bottom delay={250}>
          <div className="absolute left-[435px] top-[230px] w-[340px]">
            <p className="font-['Nunito_Sans'] text-[16px] font-black leading-[1.4] text-[#05193a]">
              {t(items[3].titleKey)}
            </p>
            <p className="mt-[10px] font-['Nunito_Sans'] text-[16px] font-light leading-[1.4] text-[#05193a]">
              {t(items[3].textKey)}
            </p>
          </div>

          </Fade>
          {/* Bottom-right: image */}
          <div className="absolute left-[812px] top-[200px] h-[200px] w-[408px] overflow-hidden">
            <img src={whyImg} alt="" className="h-full w-full object-cover" />
          </div>

          {/* Grid dividers */}
          <div className={`absolute left-[406px] top-0 h-full w-px ${DIVIDER}`} />
          <div className={`absolute left-[812px] top-0 h-full w-px ${DIVIDER}`} />
          <div className={`absolute left-0 top-[200px] h-px w-full ${DIVIDER}`} />
        </div>
      </div>

      {/* Mobile */}
      <Fade bottom>
      <div className="xl:hidden px-[20px]">
        <div className="flex flex-col items-center gap-[15px] text-center">
          <h2 className="font-['Nunito_Sans'] text-[26px] font-black leading-[1.5] text-[#3273b4]">
            {t("markets.why.title1")} {t("markets.why.title2")}
          </h2>
          <span className="block h-[4px] w-[104px] bg-[#05193a]" />
        </div>

        <div className="mt-[30px]">
          <Swiper
            modules={[Navigation]}
            onSwiper={(s) => (swiperRef.current = s)}
            spaceBetween={20}
            slidesPerView={1}
            grabCursor
          >
            {items.map((item, i) => (
              <SwiperSlide key={i}>
                <div className="border border-[#bcbcbc] px-[18px] py-[24px]">
                  <p className="font-['Nunito_Sans'] text-[16px] font-black leading-[1.4] text-[#05193a]">
                    {t(item.titleKey)}
                  </p>
                  <p className="mt-[10px] font-['Nunito_Sans'] text-[14px] font-light leading-[1.4] text-[#05193a]">
                    {t(item.textKey)}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="mt-[20px] flex justify-end gap-[10px]">
          <button type="button" aria-label="Previous" onClick={() => swiperRef.current?.slidePrev()}>
            <img src={prevButton} alt="" className="h-[42px] w-[42px]" />
          </button>
          <button type="button" aria-label="Next" onClick={() => swiperRef.current?.slideNext()}>
            <img src={nextButton} alt="" className="h-[42px] w-[42px]" />
          </button>
        </div>
      </div>
      </Fade>
    </section>
  );
}

export default WhyTrade;
